import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../components/Button/Button';
import Select from '../../components/Select/Select';
import getBitrixMarks from '../../api/cars/getBitrixMarks';
import checkBitrixMark from '../../api/cars/checkBitrixMark';
import startBitrixImport from '../../api/cars/startBitrixImport';
import getBitrixImportStatus from '../../api/cars/getBitrixImportStatus';
import alert from '../../helpers/alert';
import useOriginOptions from './useOriginOptions';
import styles from './bitrixImportPanel.module.css';

const POLL_INTERVAL_MS = 1500;

// Поля, отсутствие которых стоит показать. Идентификаторы найденных полей
// пользователю не нужны — они говорят что-то только разработчику
const IMPORTANT_FIELDS = {
  model: 'Модель',
  complectation: 'Комплектация',
  color: 'Цвет',
  year: 'Год выпуска',
  transmission: 'Тип КПП',
  engineVolume: 'Объём двигателя',
  price: 'Цена',
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const oursUnmapped = (data) => (data.dcMap || []).filter((dc) => !dc.dcId && !dc.foreign);
const foreignUnmapped = (data) => (data.dcMap || []).filter((dc) => !dc.dcId && dc.foreign);
const mapped = (data) => (data.dcMap || []).filter((dc) => dc.dcId);

const missingFields = (data) =>
  Object.entries(IMPORTANT_FIELDS)
    .filter(([key]) => !(data.resolvedFields || {})[key])
    .map(([, label]) => label);

// Индикатор ожидания. percent задан — полоса показывает реальный прогресс,
// не задан — бежит бесконечно: врать процентами там, где их неоткуда взять,
// хуже, чем честно показать «идёт работа». Счётчик секунд появляется не сразу,
// чтобы не мигать на быстрых ответах
const Waiting = ({ text, percent = null }) => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={styles.waiting}>
      <div className={styles.waitingBar}>
        <div
          className={percent === null ? styles.waitingFillRunning : styles.waitingFill}
          style={percent === null ? undefined : { width: `${percent}%` }}
        />
      </div>
      <div className={styles.waitingText}>
        {text}
        {seconds > 2 && <span className={styles.waitingSeconds}>{seconds} с</span>}
      </div>
    </div>
  );
};

const DcTable = ({ rows }) => (
  <table className={styles.dcTable}>
    <thead>
      <tr>
        <th>Отдел в Битриксе</th>
        <th>Машин</th>
        <th>Наш ДЦ</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((dc) => (
        <tr key={dc.departmentId} className={dc.dcId ? undefined : styles.dcRowUnmapped}>
          <td>
            <span className={styles.deptId}>{dc.departmentId}</span>
            {dc.departmentName || <i>название не получено</i>}
          </td>
          <td>{dc.deals}</td>
          <td>
            {dc.dcId ? (
              <>
                {dc.dcName}
                <span className={styles.dcCity}> · {dc.cityName}</span>
                {dc.manual && <span className={styles.dcManual}>вручную</span>}
              </>
            ) : dc.foreign ? (
              <span className={styles.warn}>чужой — пропущен</span>
            ) : (
              <span className={styles.warn}>не найден в справочнике</span>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const BitrixImportPanel = ({ onResult }) => {
  const [marks, setMarks] = useState([]);
  const [loadingMarks, setLoadingMarks] = useState(true);
  const [selected, setSelected] = useState([]);
  // Результат «Проверить» по каждой марке: mark -> { loading, data, error }
  const [checks, setChecks] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const originOptions = useOriginOptions();

  useEffect(() => {
    getBitrixMarks()
      .then((res) => setMarks(res?.data || []))
      .catch((err) => alert.error(errorMessage(err, 'Не удалось загрузить список марок')))
      .finally(() => setLoadingMarks(false));
  }, []);

  // Опрос статуса живёт ровно столько, сколько идёт импорт: заводится на старте и
  // гасится и по завершению, и при уходе со страницы
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const toggleMark = (mark) => {
    setSelected((prev) =>
      prev.includes(mark) ? prev.filter((m) => m !== mark) : [...prev, mark]
    );
  };

  const runCheck = async (mark) => {
    setChecks((prev) => ({ ...prev, [mark]: { loading: true } }));
    setExpanded(mark);
    try {
      const res = await checkBitrixMark(mark);
      setChecks((prev) => ({ ...prev, [mark]: { loading: false, data: res.data } }));
    } catch (err) {
      setChecks((prev) => ({
        ...prev,
        [mark]: { loading: false, error: errorMessage(err, 'Не удалось выполнить проверку') },
      }));
    }
  };

  const handleStart = async () => {
    if (selected.length === 0) return;
    try {
      const res = await startBitrixImport(selected, origin?.label || null);
      const jobId = res?.data?.jobId;
      if (!jobId) throw new Error();

      setJob({ status: 'fetching', total: 0, processed: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const status = await getBitrixImportStatus(jobId);
          const data = status.data;
          setJob(data);

          if (data.status === 'done') {
            stopPolling();
            // Предупреждения показываем здесь же, рядом с марками — наверх не отдаём,
            // иначе страница отрисует их вторым экземпляром
            onResult(data.result);
            alert.success(`Импорт завершён. Загружено машин: ${data.result?.imported ?? 0}`);
          }
          if (data.status === 'error') {
            stopPolling();
            alert.error(data.error || 'Импорт завершился ошибкой');
          }
        } catch (err) {
          stopPolling();
          setJob((prev) => ({ ...prev, status: 'error', error: errorMessage(err, 'Связь с задачей импорта потеряна') }));
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      alert.error(errorMessage(err, 'Не удалось запустить импорт'));
    }
  };

  const running = job?.status === 'fetching' || job?.status === 'importing';
  const percent = job?.total > 0 ? Math.round((100 * job.processed) / job.total) : 0;

  // Итоги по проверенным маркам из числа выбранных. Пока проверены не все,
  // числа не показываем — врать о количестве хуже, чем не называть его
  const checkedSelected = selected.map((m) => checks[m]?.data).filter(Boolean);
  const allChecked = checkedSelected.length === selected.length && selected.length > 0;
  const willImport = allChecked ? checkedSelected.reduce((sum, d) => sum + d.rows, 0) : null;
  const manualRows = checkedSelected.reduce((sum, d) => sum + (d.manualDc || 0), 0);
  const skippedRows = checkedSelected.reduce((sum, d) => sum + (d.skippedForeign || 0), 0);

  if (loadingMarks) {
    return <div className={styles.placeholder}>Загружаем список марок…</div>;
  }

  if (marks.length === 0) {
    return (
      <div className={styles.placeholder}>
        <div className={styles.placeholderTitle}>Ни одна марка не подключена</div>
        <p>
          Импорт настраивается на бэкенде в <code>appsettings.json</code>, ключ{' '}
          <code>BITRIX_STOCK</code> — по одной записи на марку: вебхук портала с правами{' '}
          <code>crm</code> и <code>department</code>, идентификатор воронки и список стадий,
          машины которых доступны к продаже.
        </p>
        <p className={styles.placeholderHint}>
          Порядок подключения новой марки описан в{' '}
          <code>doscar_back/docs/bitrix-stock-import.md</code>
        </p>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.markList}>
        {marks.map((m) => {
          const check = checks[m.mark];
          const isOpen = expanded === m.mark;
          return (
            <div key={m.mark} className={styles.markCard}>
              <div className={styles.markRow}>
                <label className={styles.markPick}>
                  <input
                    type="checkbox"
                    checked={selected.includes(m.mark)}
                    disabled={running || !m.knownMark}
                    onChange={() => toggleMark(m.mark)}
                  />
                  <span className={styles.markName}>{m.mark}</span>
                </label>

                <div className={styles.markMeta}>
                  {/* Показываем не настройки, а только то, на что пользователь может
                      среагировать: без белого списка стадий в сток уедут и резерв,
                      и дефектные машины */}
                  {!m.stages?.length && (
                    <span className={styles.warn}>
                      Стадии не заданы: будут импортированы все незакрытые сделки, включая
                      зарезервированные и дефектные машины
                    </span>
                  )}
                </div>

                <Button
                  text={check?.loading ? 'Проверяем…' : 'Проверить'}
                  onClick={() => runCheck(m.mark)}
                  disabled={running || check?.loading}
                />
              </div>

              {check?.loading && (
                <Waiting text="Запрашиваем данные из Битрикса: поля, сделки, структуру компании" />
              )}

              {!m.knownMark && (
                <div className={styles.markError}>
                  Марка «{m.mark}» отсутствует в справочнике — импортировать нечего. Добавьте её в
                  раздел марок или исправьте название в настройках.
                </div>
              )}

              {isOpen && check?.error && <div className={styles.markError}>{check.error}</div>}

              {isOpen && check?.data && (
                <div className={styles.checkResult}>
                  <div className={styles.checkStats}>
                    <span>
                      Сделок в воронке: <b>{check.data.totalDeals}</b>
                    </span>
                    <span>
                      Будут импортированы: <b>{check.data.rows - (check.data.manualDc || 0)}</b>
                    </span>
                    {check.data.manualDc > 0 && (
                      <span className={styles.warn}>
                        требуют ручной привязки ДЦ: {check.data.manualDc}
                      </span>
                    )}
                    {check.data.skippedForeign > 0 && (
                      <span className={styles.warn}>
                        чужие дилеры пропущены: {check.data.skippedForeign}
                      </span>
                    )}
                  </div>

                  {/* Разбираться нужно только с несопоставленными; успешные строки
                      после первой проверки — шум, поэтому они свёрнуты */}
                  {oursUnmapped(check.data).length > 0 && (
                    <>
                      <DcTable rows={oursUnmapped(check.data)} />
                      <div className={styles.hint}>
                        Эти дилерские центры похожи на наши, но в справочнике нет однозначной
                        записи. Машины будут импортированы, центр у каждой потребуется указать
                        вручную. Чтобы это не повторялось, добавьте центр в справочник или
                        свяжите отдел в настройках.
                        <details className={styles.spoiler}>
                          <summary>Как связать в настройках</summary>
                          В <code>BITRIX_STOCK</code> этой марки:{' '}
                          <code>{'"dcMap": { "<id отдела>": <id нашего ДЦ> }'}</code>
                        </details>
                      </div>
                    </>
                  )}

                  {foreignUnmapped(check.data).length > 0 && (
                    <details className={styles.mappedBlock}>
                      <summary>
                        Чужие дилеры портала, не импортируются:{' '}
                        {foreignUnmapped(check.data).length}
                      </summary>
                      <DcTable rows={foreignUnmapped(check.data)} />
                    </details>
                  )}

                  {mapped(check.data).length > 0 && (
                    <details className={styles.mappedBlock}>
                      <summary>Найдены в справочнике: {mapped(check.data).length}</summary>
                      <DcTable rows={mapped(check.data)} />
                    </details>
                  )}

                  {missingFields(check.data).length > 0 && (
                    <div className={styles.markError}>
                      На портале не найдены поля: {missingFields(check.data).join(', ')}. Эти
                      значения останутся пустыми.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={styles.originBlock}>
        <Select
          title="Происхождение автомобилей"
          placeholder="Не выбрано — потребуется указать вручную"
          options={originOptions}
          value={origin}
          onChange={setOrigin}
          disabled={running}
        />
        <div className={styles.hint}>
          В Битриксе это поле отсутствует. Выбранное значение будет проставлено всем
          импортируемым машинам.
        </div>
      </div>

      {selected.length > 0 && !running && (
        <div className={styles.replaceNotice}>
          Импорт заменит сток выбранных марок: машины, отсутствующие в выгрузке, будут сняты
          с витрины. Дилерские центры без поступлений не затрагиваются.
          {manualRows > 0 &&
            ` У ${manualRows} машин не определён дилерский центр — их потребуется исправить вручную.`}
          {skippedRows > 0 && ` Чужие дилеры (${skippedRows} машин) не импортируются.`}
        </div>
      )}

      <div className={styles.actions}>
        <Button
          fill
          text={willImport !== null ? `Импортировать (${willImport})` : 'Импортировать'}
          onClick={handleStart}
          disabled={running || selected.length === 0}
        />
      </div>

      {job && running && (
        <div className={styles.progress}>
          {job.status === 'fetching' ? (
            <>
              <Waiting
                text={
                  `Загружаем сделки из Битрикса${job.currentMark ? ` · ${job.currentMark}` : ''}` +
                  (job.total > 0 ? ` — ${job.processed} из ${job.total}` : '')
                }
                percent={job.total > 0 ? percent : null}
              />
              <div className={styles.hint}>
                Сток не изменяется до завершения загрузки
              </div>
            </>
          ) : (
            // Импортёр отдаёт результат целиком, построчного прогресса у него нет —
            // поэтому полоса бежит, а не замирает на ста процентах
            <Waiting
              text={
                willImport !== null
                  ? `Сохраняем в сток: ${willImport} машин`
                  : 'Сохраняем в сток'
              }
            />
          )}
        </div>
      )}

      {job?.status === 'error' && <div className={styles.markError}>{job.error}</div>}

      {job?.warnings?.length > 0 && job.status === 'done' && (
        <div className={styles.warnings}>
          {job.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BitrixImportPanel;
